function normalizeText(value) {
  return (value || "").trim();
}

function isUnknownRegion(value) {
  const text = normalizeText(value);
  return !text || text === "未知" || text === "x";
}

/** 直辖市中间层，对用户展示无意义 */
const SKIP_CITY_LABELS = new Set(["市辖区", "县"]);

/**
 * 格式化省市区展示文案。
 * 普通：安徽省 · 芜湖市 · 南陵县
 * 直辖市：上海市 · 浦东新区（跳过「市辖区」）
 */
export function formatLocationDisplay(
  province,
  city,
  district,
  { separator = " · ", fallback = "" } = {}
) {
  const p = normalizeText(province);
  const c = normalizeText(city);
  const d = normalizeText(district);

  const parts = [];
  if (p) parts.push(p);

  const cityIsRedundant =
    !c ||
    SKIP_CITY_LABELS.has(c) ||
    (p && (c === p || p.startsWith(c) || c.startsWith(p.replace(/(省|市|自治区|特别行政区)$/, ""))));

  if (c && !cityIsRedundant) {
    parts.push(c);
  }

  if (d && d !== c && d !== p) {
    parts.push(d);
  }

  const unique = [];
  for (const part of parts) {
    if (!unique.length || unique[unique.length - 1] !== part) {
      unique.push(part);
    }
  }

  return unique.length ? unique.join(separator) : fallback;
}

/**
 * 卡片/列表用的短地址：优先完整省市区，避免只显示「市辖区」。
 */
export function formatCityLabel(province, city, district, fallback = "地区未填") {
  return (
    formatLocationDisplay(province, city, district, { separator: " · " }) ||
    normalizeText(city) ||
    normalizeText(province) ||
    fallback
  );
}

/**
 * 尝试用分隔符拆分：安徽省/芜湖市/南陵县、安徽省,芜湖市,南陵县 等
 */
function splitBySeparators(text) {
  const parts = text
    .split(/[/|,，、|\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts : null;
}

/**
 * 从单个合并字符串（通常存在旧版 city 字段）解析省/市/区
 */
export function parseCombinedRegionText(text, regions) {
  const input = normalizeText(text);
  if (isUnknownRegion(input)) {
    return null;
  }

  const separated = splitBySeparators(input);
  if (separated) {
    const [provincePart, cityPart, ...districtParts] = separated;
    const districtPart = districtParts.join("");
    const resolved = matchRegionParts(
      provincePart,
      cityPart,
      districtPart,
      regions
    );
    if (resolved.province && resolved.city) {
      return resolved;
    }
  }

  for (const province of regions) {
    if (!input.includes(province.name)) {
      continue;
    }

    for (const city of province.children || []) {
      if (!input.includes(city.name)) {
        continue;
      }

      for (const district of city.children || []) {
        if (input.includes(district.name)) {
          return {
            province: province.name,
            city: city.name,
            district: district.name,
          };
        }
      }

      return {
        province: province.name,
        city: city.name,
        district: "",
      };
    }

    const firstCity = province.children?.[0]?.name || "";
    return {
      province: province.name,
      city: firstCity,
      district: "",
    };
  }

  return null;
}

function matchRegionParts(provincePart, cityPart, districtPart, regions) {
  let province = normalizeText(provincePart);
  let city = normalizeText(cityPart);
  let district = normalizeText(districtPart);

  for (const p of regions) {
    const provinceMatched =
      p.name === province ||
      p.name.includes(province) ||
      province.includes(p.name.replace(/(省|市|自治区|特别行政区)$/, ""));

    if (!provinceMatched) {
      continue;
    }

    province = p.name;

    for (const c of p.children || []) {
      const cityMatched =
        c.name === city ||
        c.name.includes(city) ||
        city.includes(c.name.replace(/(市|地区|自治州|盟)$/, ""));

      if (!cityMatched) {
        continue;
      }

      city = c.name;

      if (district) {
        for (const d of c.children || []) {
          if (
            d.name === district ||
            d.name.includes(district) ||
            district.includes(d.name.replace(/(区|县|市)$/, ""))
          ) {
            district = d.name;
            break;
          }
        }
      }

      return { province, city, district };
    }

    return { province, city, district };
  }

  return { province, city, district };
}

function matchRegionInTree(province, city, district, regions) {
  const normalizedProvince = normalizeText(province);
  const normalizedCity = normalizeText(city);
  const normalizedDistrict = normalizeText(district);

  if (normalizedProvince && normalizedCity) {
    return {
      province: normalizedProvince,
      city: normalizedCity,
      district: normalizedDistrict,
    };
  }

  if (!normalizedCity && !normalizedProvince) {
    return { province: "", city: "", district: "" };
  }

  for (const p of regions) {
    if (p.name === normalizedCity) {
      const firstCity = p.children?.[0]?.name || "";
      return {
        province: p.name,
        city: firstCity,
        district: normalizedDistrict,
      };
    }

    for (const c of p.children || []) {
      if (c.name === normalizedCity) {
        if (normalizedDistrict) {
          return {
            province: p.name,
            city: c.name,
            district: normalizedDistrict,
          };
        }

        return {
          province: p.name,
          city: c.name,
          district: "",
        };
      }

      for (const d of c.children || []) {
        if (d.name === normalizedDistrict) {
          return {
            province: p.name,
            city: c.name,
            district: d.name,
          };
        }
      }
    }
  }

  return {
    province: normalizedProvince,
    city: normalizedCity,
    district: normalizedDistrict,
  };
}

/**
 * 兼容三种存储方式：
 * 1. 省 / 市 / 区 三个字段（新数据）
 * 2. 仅 city 字段存合并地址，如「安徽省芜湖市南陵县」
 * 3. 仅有 city 字段存城市名，自动反查省份
 */
export function resolveRegionFromDb(province, city, district, regions) {
  const normalizedProvince = normalizeText(province);
  const normalizedCity = normalizeText(city);
  const normalizedDistrict = normalizeText(district);

  if (normalizedProvince && normalizedCity) {
    return matchRegionInTree(
      normalizedProvince,
      normalizedCity,
      normalizedDistrict,
      regions
    );
  }

  if (
    !normalizedProvince &&
    normalizedCity &&
    !isUnknownRegion(normalizedCity)
  ) {
    const parsed = parseCombinedRegionText(normalizedCity, regions);
    if (parsed) {
      return parsed;
    }
  }

  if (
    normalizedProvince &&
    !normalizedCity &&
    !isUnknownRegion(normalizedProvince)
  ) {
    const parsed = parseCombinedRegionText(normalizedProvince, regions);
    if (parsed) {
      return parsed;
    }
  }

  return matchRegionInTree(
    normalizedProvince,
    isUnknownRegion(normalizedCity) ? "" : normalizedCity,
    normalizedDistrict,
    regions
  );
}
